/*
 * Banco local do eloOficina Web (IndexedDB).
 * Equivalente às tabelas tblinspecao / tblinspecaofoto do app React Native:
 * as fotos ficam guardadas no navegador até o envio ser concluído.
 */
const DB_NAME = 'elooficina';
const DB_VERSION = 1;
const STORE_INSPECAO = 'inspecao';
const STORE_FOTO = 'inspecaoFoto';

let conexao = null;

const promessa = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const finalizarTransacao = (tx) => new Promise((resolve, reject) => {
  tx.oncomplete = () => resolve();
  tx.onabort = () => reject(tx.error);
  tx.onerror = () => reject(tx.error);
});

export const abrirBanco = () => {
  if (conexao) return conexao;

  conexao = new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('INDEXEDDB_INDISPONIVEL'));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_INSPECAO)) {
        const inspecao = db.createObjectStore(STORE_INSPECAO, { keyPath: 'id', autoIncrement: true });
        inspecao.createIndex('status', 'status', { unique: false });
        inspecao.createIndex('placa', 'placa', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_FOTO)) {
        const foto = db.createObjectStore(STORE_FOTO, { keyPath: 'id', autoIncrement: true });
        foto.createIndex('inspecaoId', 'inspecaoId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      conexao = null;
      reject(request.error);
    };
  });

  return conexao;
};

/*
 * Observação: cada transação só executa requisições síncronas entre si
 * (sem "await" no meio), para evitar o auto-commit precoce em alguns
 * navegadores (principalmente Safari/iOS).
 */
const ler = async (stores, executar) => {
  const db = await abrirBanco();
  const tx = db.transaction(stores, 'readonly');
  return promessa(executar(tx));
};

const escrever = async (stores, executar) => {
  const db = await abrirBanco();
  const tx = db.transaction(stores, 'readwrite');
  const request = executar(tx);
  const [resultado] = await Promise.all([request ? promessa(request) : null, finalizarTransacao(tx)]);
  return resultado;
};

const chavesFotos = (inspecaoId) => ler(STORE_FOTO, (tx) => tx.objectStore(STORE_FOTO).index('inspecaoId').getAllKeys(inspecaoId));

export const criarOuObterInspecao = async (placa) => {
  const existentes = await ler(STORE_INSPECAO, (tx) => tx.objectStore(STORE_INSPECAO).index('placa').getAll(placa));
  const pendente = existentes
    .filter((item) => item.status === 'PENDENTE')
    .sort((a, b) => b.id - a.id)[0];

  if (pendente) return pendente.id;

  return escrever(STORE_INSPECAO, (tx) => tx.objectStore(STORE_INSPECAO).add({
    placa,
    criadoEm: new Date().toISOString(),
    enviadoEm: null,
    status: 'PENDENTE',
  }));
};

export const obterInspecao = (inspecaoId) => ler(STORE_INSPECAO, (tx) => tx.objectStore(STORE_INSPECAO).get(inspecaoId));

export const listarFotos = async (inspecaoId) => {
  const fotos = await ler(STORE_FOTO, (tx) => tx.objectStore(STORE_FOTO).index('inspecaoId').getAll(inspecaoId));
  return fotos.sort((a, b) => b.id - a.id);
};

export const listarInspecoesPendentes = async () => {
  const inspecoes = await ler(STORE_INSPECAO, (tx) => tx.objectStore(STORE_INSPECAO).index('status').getAll('PENDENTE'));
  const resultado = await Promise.all(inspecoes.map(async (inspecao) => ({
    ...inspecao,
    quantidadeFotos: await ler(STORE_FOTO, (tx) => tx.objectStore(STORE_FOTO).index('inspecaoId').count(inspecao.id)),
  })));
  return resultado.sort((a, b) => b.id - a.id);
};

export const salvarFoto = (inspecaoId, blob) => escrever(STORE_FOTO, (tx) => tx.objectStore(STORE_FOTO).add({
  inspecaoId,
  blob,
  criadoEm: new Date().toISOString(),
}));

export const excluirFoto = (fotoId) => escrever(STORE_FOTO, (tx) => tx.objectStore(STORE_FOTO).delete(fotoId));

export const excluirInspecao = async (inspecaoId) => {
  const fotos = await chavesFotos(inspecaoId);
  await escrever([STORE_INSPECAO, STORE_FOTO], (tx) => {
    fotos.forEach((id) => tx.objectStore(STORE_FOTO).delete(id));
    tx.objectStore(STORE_INSPECAO).delete(inspecaoId);
    return null;
  });
};

/** Marca a inspeção como ENVIADA e remove as fotos guardadas no navegador. */
export const concluirEnvio = async (inspecaoId) => {
  const inspecao = await obterInspecao(inspecaoId);
  const fotos = await chavesFotos(inspecaoId);
  await escrever([STORE_INSPECAO, STORE_FOTO], (tx) => {
    if (inspecao) {
      tx.objectStore(STORE_INSPECAO).put({ ...inspecao, status: 'ENVIADA', enviadoEm: new Date().toISOString() });
    }
    fotos.forEach((id) => tx.objectStore(STORE_FOTO).delete(id));
    return null;
  });
};
